"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Dispatch } from "react";
import { createPortal } from "react-dom";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
  type Connection,
  type NodeTypes,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import type { FunnelWizardState, FunnelGraph, FunnelGraphNode, FunnelGraphEdge } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { AppLocale } from "@/lib/funnel-planner/schema";
import {
  buildInitialGraph,
  computeGraphVolumes,
  graphToStages,
  stageColor,
} from "@/lib/funnel-planner/graph-utils";
import { buildPlannerStageShareUrl } from "@/lib/funnel-planner/share-url";
import { totalVisitorsFromChannels } from "@/lib/funnel-planner/engine";
import { noteDraftAction } from "@/lib/funnel-planner/storage";
import {
  channelLabel,
  directionForFunnelLocale,
  formatFunnelNumber,
  formatFunnelPercent,
  funnelText,
  stageTypeLabel,
  textAlignForFunnelLocale,
} from "@/lib/funnel-planner/i18n";
import { track } from "@/lib/posthog/events";
import {
  ALL_STAGE_TYPE_IDS,
  getStageTypeMeta as getCatalogStageTypeMeta,
  type StageTypeMeta,
} from "@/lib/funnel-planner/stageTypes";
import { StageTypePicker, DEFAULT_RECOMMENDED_STAGE_TYPES } from "@/components/funnel-planner/ui/StageTypePicker";
import { TrafficSourceNode } from "./TrafficSourceNode";
import { StageNode } from "./StageNode";
import {
  NODE_COLOR_SWATCHES,
  SOURCE_NODE_DEFAULT_HEIGHT,
  SOURCE_NODE_DEFAULT_WIDTH,
  SOURCE_NODE_MAX_HEIGHT,
  SOURCE_NODE_MAX_WIDTH,
  SOURCE_NODE_MIN_HEIGHT,
  SOURCE_NODE_MIN_WIDTH,
  STAGE_NODE_DEFAULT_HEIGHT,
  STAGE_NODE_DEFAULT_WIDTH,
  STAGE_NODE_MAX_HEIGHT,
  STAGE_NODE_MAX_WIDTH,
  STAGE_NODE_MIN_HEIGHT,
  STAGE_NODE_MIN_WIDTH,
  clampNodeDimension,
  normalizeNodeColor,
} from "./node-visuals";
import {
  joinAuditTicketGroups,
  STEP_FIVE_CTA_TICKETS,
  STEP_FIVE_FIELD_INPUT_TICKETS,
  STEP_FIVE_FUNNEL_STAGE_MAP_SUPPORT_TICKETS,
  STEP_FIVE_STAGE_CONVERSION_CONTROLS_SUPPORT_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

const NODE_TYPES: NodeTypes = {
  trafficSource: TrafficSourceNode,
  stage: StageNode,
};
const STAGE_CONVERSION_CONTROLS_READINESS_TICKET =
  "P1-520001-interactive-step-05-stage-conversion-controls-readiness-board";
const STAGE_CONVERSION_CONTROLS_OFFER_ASSEMBLY_TICKET =
  "P3-520002-interactive-step-05-stage-conversion-controls-offer-assembly-configurator";
const FUNNEL_STAGE_MAP_READINESS_TICKET =
  "P1-521001-interactive-step-05-funnel-stage-map-readiness-board";
const FUNNEL_STAGE_MAP_OFFER_ASSEMBLY_TICKET =
  "P3-521002-interactive-step-05-funnel-stage-map-offer-assembly-configurator";
const READINESS_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";
const COMPACT_CANVAS_SHELLS = new Set(["mobile", "foldable", "watch", "ereader"]);
const STEP_FIVE_OFFER_ASSEMBLY_CTA_TICKETS = [
  "P1-443001-cta-step-05-button-planner-action-024-offer-assembly-mini-cta-brief",
  "P1-444001-cta-step-05-button-planner-action-025-offer-assembly-mini-cta-brief",
  "P1-445001-cta-step-05-button-planner-action-026-offer-assembly-mini-cta-brief",
  "P1-446001-cta-step-05-button-planner-action-027-offer-assembly-mini-cta-brief",
  "P1-447001-cta-step-05-button-planner-action-028-offer-assembly-mini-cta-brief",
  "P1-448001-cta-step-05-button-planner-action-029-offer-assembly-mini-cta-brief",
  "P1-449001-cta-step-05-button-planner-action-030-offer-assembly-mini-cta-brief",
  "P1-450001-cta-step-05-button-planner-action-031-offer-assembly-mini-cta-brief",
  "P1-451001-cta-step-05-button-planner-action-032-offer-assembly-mini-cta-brief",
  "P1-452001-cta-step-05-button-planner-action-033-offer-assembly-mini-cta-brief",
  "P1-453001-cta-step-05-button-planner-action-034-offer-assembly-mini-cta-brief",
  "P1-454001-cta-step-05-button-planner-action-035-offer-assembly-mini-cta-brief",
  "P1-455001-cta-step-05-button-planner-action-036-offer-assembly-mini-cta-brief",
  "P1-456001-cta-step-05-button-planner-action-037-offer-assembly-mini-cta-brief",
  "P1-457001-cta-step-05-button-planner-action-038-offer-assembly-mini-cta-brief",
  "P1-458001-cta-step-05-button-planner-action-039-offer-assembly-mini-cta-brief",
  "P1-459001-cta-step-05-button-planner-action-040-offer-assembly-mini-cta-brief",
  "P1-460001-cta-step-05-button-planner-action-041-offer-assembly-mini-cta-brief",
] as const;
const STEP_FIVE_STAGE_ACTION_TICKETS = [
  ...STEP_FIVE_OFFER_ASSEMBLY_CTA_TICKETS,
  ...STEP_FIVE_CTA_TICKETS,
] as const;
const STAGE_CONVERSION_CONTROLS_AUDIT_TICKETS = joinAuditTicketGroups(
  STAGE_CONVERSION_CONTROLS_READINESS_TICKET,
  STAGE_CONVERSION_CONTROLS_OFFER_ASSEMBLY_TICKET,
  STEP_FIVE_FIELD_INPUT_TICKETS,
  STEP_FIVE_STAGE_CONVERSION_CONTROLS_SUPPORT_TICKETS,
);
const FUNNEL_STAGE_MAP_AUDIT_TICKETS = joinAuditTicketGroups(
  FUNNEL_STAGE_MAP_READINESS_TICKET,
  FUNNEL_STAGE_MAP_OFFER_ASSEMBLY_TICKET,
  STEP_FIVE_FUNNEL_STAGE_MAP_SUPPORT_TICKETS,
);

function stepFiveOfferAssemblyCtaTicketsForAction(actionIndex: number, actionCount: number): string {
  return STEP_FIVE_STAGE_ACTION_TICKETS
    .filter((_, ticketIndex) => ticketIndex % Math.max(actionCount, 1) === actionIndex)
    .join(";");
}

function stepFiveOfferAssemblyCtaAttributes(ticketGroup: string) {
  return {
    "data-funnel-planner-offer-assembly-cta": "true",
    "data-funnel-planner-step-five-offer-assembly-cta": "true",
    "data-audit-ticket": ticketGroup,
    "data-tier-one-family": "offer_assembly_mini_cta_brief",
    "data-gamification-current-state": "active_inprogress",
    "data-gamification-reduced-motion-static": "supported",
    "data-gamification-supported-states": READINESS_SUPPORTED_STATES,
    "data-readiness-ready-count": 0,
    "data-readiness-total-count": 1,
    "data-readiness-tier": "brief_piece_available",
    "data-blockers-bucket": "one",
  } as const;
}

const STAGE_TYPE_OPTIONS = ALL_STAGE_TYPE_IDS.map((type) => {
  const meta = getCatalogStageTypeMeta(type);
  return {
    type,
    label: meta.label,
    icon: meta.icon,
    group: meta.group,
    defaultCvr: meta.defaultCvr,
    description: meta.description,
  };
});

const STAGE_GROUP_ORDER: Array<StageTypeMeta["group"]> = [
  "acquisition",
  "nurture",
  "conversion",
  "retention",
  "plg",
  "utility",
];

const NODE_KIND_OPTIONS = [
  { value: "stage", label: "Standard stage" },
  { value: "branch", label: "Branch / split" },
  { value: "retargeting", label: "Retargeting step" },
  { value: "wait", label: "Wait / delay" },
  { value: "survey", label: "Survey / quiz split" },
];

const TRACKING_STATUS_OPTIONS = [
  { value: "none", label: "No tracking" },
  { value: "planned", label: "Tracking planned" },
  { value: "installed", label: "Pixel installed" },
  { value: "verified", label: "Tracking verified" },
  { value: "broken", label: "Tracking broken" },
];

const PUBLISHED_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "live", label: "Live" },
  { value: "paused", label: "Paused" },
];

const EDGE_TYPE_OPTIONS = [
  { value: "main", label: "Main path" },
  { value: "yes", label: "Yes branch" },
  { value: "no", label: "No branch" },
  { value: "retargeting", label: "Retargeting loop" },
  { value: "wait", label: "Wait/delay path" },
  { value: "survey", label: "Survey answer split" },
  { value: "optional", label: "Optional path" },
];

const SOURCE_CHANNEL_OPTIONS = [
  { channel: "google_organic",     label: "Google Organic",       icon: "G",   color: "#4285f4" },
  { channel: "bing_organic",       label: "Bing Organic",         icon: "B",   color: "#0078d4" },
  { channel: "other_search",       label: "Other Search",         icon: "S",   color: "#64748b" },
  { channel: "google_ads",         label: "Google Ads",           icon: "G$",  color: "#ea4335" },
  { channel: "bing_ads",           label: "Bing Ads",             icon: "B$",  color: "#0078d4" },
  { channel: "ppc_source",         label: "PPC",                  icon: "P$",  color: "#f97316" },
  { channel: "instagram",          label: "Instagram Organic",     icon: "IG",  color: "#c13584" },
  { channel: "linkedin",           label: "LinkedIn",             icon: "in",  color: "#0a66c2" },
  { channel: "youtube",            label: "YouTube Organic",      icon: "YT",  color: "#ff0000" },
  { channel: "tiktok",             label: "TikTok",               icon: "TT",  color: "#010101" },
  { channel: "pinterest",          label: "Pinterest",            icon: "P",   color: "#e60023" },
  { channel: "facebook_ads",       label: "Facebook Ads",         icon: "f$",  color: "#1877f2" },
  { channel: "linkedin_ads",       label: "LinkedIn Ads",         icon: "in$", color: "#0a66c2" },
  { channel: "youtube_ads",        label: "YouTube Ads",          icon: "YT$", color: "#ff0000" },
  { channel: "email_newsletter",   label: "Email Newsletter",     icon: "EM",  color: "#10b981" },
  { channel: "email_ads",          label: "Email Ads",            icon: "EM$", color: "#0ea5e9" },
  { channel: "blog_articles",      label: "Blog / Articles",      icon: "A",   color: "#8b5cf6" },
  { channel: "referral",           label: "Referral",             icon: "R",   color: "#f59e0b" },
  { channel: "affiliates",         label: "Affiliates",           icon: "AF",  color: "#06b6d4" },
  { channel: "retargeting",        label: "Retargeting",          icon: "RTG", color: "#7c3aed" },
  { channel: "direct_mail",        label: "Direct Mail",          icon: "DM",  color: "#0369a1" },
  { channel: "review_sites",       label: "Review Sites",         icon: "RV",  color: "#d97706" },
  { channel: "radio_ad",           label: "Radio Ad",             icon: "RA",  color: "#dc2626" },
  { channel: "podcast",            label: "Podcast Organic",      icon: "PO",  color: "#7c3aed" },
  { channel: "sms",                label: "SMS / Text",           icon: "SMS", color: "#0891b2" },
  { channel: "custom",             label: "Custom Source",        icon: "C",   color: "#6b7280" },
];

function sourceChannelColor(channel: unknown): string {
  const normalized = typeof channel === "string" ? channel : "custom";
  return SOURCE_CHANNEL_OPTIONS.find(option => option.channel === normalized)?.color ?? "#2563eb";
}

function nodeDefaultColor(node: Node): string {
  const data = node.data as Record<string, unknown>;
  const customColor = normalizeNodeColor(data.color);
  if (customColor) return customColor;
  if (node.type === "trafficSource") return sourceChannelColor(data.channel);
  return stageColor(typeof data.stageType === "string" ? data.stageType : undefined);
}

function nodeBoxLimits(nodeType: string | undefined) {
  if (nodeType === "trafficSource") {
    return {
      defaultWidth: SOURCE_NODE_DEFAULT_WIDTH,
      defaultHeight: SOURCE_NODE_DEFAULT_HEIGHT,
      minWidth: SOURCE_NODE_MIN_WIDTH,
      minHeight: SOURCE_NODE_MIN_HEIGHT,
      maxWidth: SOURCE_NODE_MAX_WIDTH,
      maxHeight: SOURCE_NODE_MAX_HEIGHT,
    };
  }
  return {
    defaultWidth: STAGE_NODE_DEFAULT_WIDTH,
    defaultHeight: STAGE_NODE_DEFAULT_HEIGHT,
    minWidth: STAGE_NODE_MIN_WIDTH,
    minHeight: STAGE_NODE_MIN_HEIGHT,
    maxWidth: STAGE_NODE_MAX_WIDTH,
    maxHeight: STAGE_NODE_MAX_HEIGHT,
  };
}

type Action =
  | { type: "SET_STAGES"; stages: FunnelWizardState["stages"] }
  | { type: "SET_GRAPH"; graph: FunnelGraph; source?: "automatic" };

type CanvasInnerProps = {
  state: FunnelWizardState;
  dispatch: Dispatch<Action>;
  content: FunnelPlannerContent;
  locale: AppLocale;
  pendingSyncRef: React.MutableRefObject<boolean>;
  flushRef?: React.MutableRefObject<(() => void) | null>;
};

type FlowPoint = {
  x: number;
  y: number;
};

type CanvasContextMenu =
  | { type: "node"; x: number; y: number; nodeId: string }
  | { type: "edge"; x: number; y: number; edgeId: string }
  | { type: "pane"; x: number; y: number; flowPosition: FlowPoint };

type CanvasEdgeData = {
  edgeType?: string;
  splitPercent?: number;
  edgeCvr?: number;
  optional?: boolean;
};

type NodePreset = {
  label: string;
  stageType: string;
  nodeKind: string;
  conversionRate?: number;
  waitDays?: number;
  optionalStage?: boolean;
};

const CANVAS_STYLE_TOKENS = `
  :root {
    --color-canvas-bg: #0f172a;
    --color-panel: #1e293b;
    --color-border-subtle: #334155;
    --color-border-mid: #475569;
    --color-text: #e2e8f0;
    --color-muted: #cbd5e1;
    --color-muted-dim: #64748b;
    --color-accent-edge: #818cf8;
    --color-accent-primary: #2563eb;
    --color-accent-hover: #3b82f6;
    --color-accent-stage: #6366f1;
    --color-accent-source: #1e40af;
    --color-error: #ef4444;
    --color-success: #22c55e;
    --color-active-bg: rgba(37,99,235,0.10);
  }

  .react-flow__resize-control.funnel-node-resize-line {
    border-width: 0 !important;
    z-index: 24;
  }

  .react-flow__resize-control.funnel-node-resize-handle {
    z-index: 25;
  }

  .react-flow__resize-control.funnel-node-resize-line.left,
  .react-flow__resize-control.funnel-node-resize-line.right {
    width: 12px;
  }

  .react-flow__resize-control.funnel-node-resize-line.top,
  .react-flow__resize-control.funnel-node-resize-line.bottom {
    height: 12px;
  }

  .react-flow__resize-control.funnel-node-resize-line::after {
    content: "";
    position: absolute;
    pointer-events: none;
    border-color: inherit;
    border-style: solid;
    border-width: 0;
  }

  .react-flow__resize-control.funnel-node-resize-line.left::after,
  .react-flow__resize-control.funnel-node-resize-line.right::after {
    top: 0;
    bottom: 0;
    left: 50%;
  }

  .react-flow__resize-control.funnel-node-resize-line.left::after {
    border-left-width: 1px;
  }

  .react-flow__resize-control.funnel-node-resize-line.right::after {
    border-right-width: 1px;
  }

  .react-flow__resize-control.funnel-node-resize-line.top::after,
  .react-flow__resize-control.funnel-node-resize-line.bottom::after {
    left: 0;
    right: 0;
    top: 50%;
  }

  .react-flow__resize-control.funnel-node-resize-line.top::after {
    border-top-width: 1px;
  }

  .react-flow__resize-control.funnel-node-resize-line.bottom::after {
    border-bottom-width: 1px;
  }
`;

const CTX_INPUT: React.CSSProperties = {
  width: "100%",
  fontSize: 12,
  background: "var(--color-canvas-bg)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 5,
  color: "var(--color-text)",
  caretColor: "var(--color-text)",
  colorScheme: "dark",
  padding: "5px 8px",
  outline: "2px solid transparent",
  marginBottom: 10,
  boxSizing: "border-box",
  textAlign: "start",
};

const CTX_SELECT: React.CSSProperties = {
  width: "100%",
  fontSize: 12,
  background: "var(--color-canvas-bg)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 5,
  color: "var(--color-text)",
  colorScheme: "dark",
  padding: "5px 8px",
  outline: "2px solid transparent",
  marginBottom: 10,
  textAlign: "start",
};

const CTX_LABEL: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--color-muted)",
  marginBottom: 3,
};

const CTX_CHECKBOX_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: "var(--color-text)",
  fontSize: 12,
  marginBottom: 10,
};

const CTX_MENU_BUTTON: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  width: "100%",
  padding: "8px 10px",
  borderRadius: 7,
  background: "transparent",
  border: "1px solid transparent",
  color: "var(--color-text)",
  fontSize: 12,
  fontWeight: 650,
  cursor: "pointer",
  textAlign: "start",
};

async function copyCanvasTextToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy selection path.
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

function makeEdgeLabel(data: CanvasEdgeData): string | undefined {
  const edgeType = data.edgeType && data.edgeType !== "main"
    ? EDGE_TYPE_OPTIONS.find(option => option.value === data.edgeType)?.label ?? data.edgeType
    : "";
  const split = typeof data.splitPercent === "number" && data.splitPercent >= 0 && data.splitPercent < 100
    ? `${data.splitPercent}%`
    : "";
  const cvr = typeof data.edgeCvr === "number" && data.edgeCvr >= 0 && data.edgeCvr < 100
    ? `${data.edgeCvr}% CVR`
    : "";
  return [edgeType, split, cvr].filter(Boolean).join(" | ") || undefined;
}

function decorateEdge(edge: Edge): Edge {
  const data = (edge.data ?? {}) as CanvasEdgeData;
  const type = data.edgeType ?? "main";
  const stroke = type === "retargeting"
    ? "#a78bfa"
    : type === "wait"
      ? "#f59e0b"
      : type === "yes"
        ? "#22c55e"
        : type === "no"
          ? "#ef4444"
          : "var(--color-accent-edge, #818cf8)";

  return {
    ...edge,
    animated: type === "retargeting" || type === "wait" || edge.animated,
    type: edge.type ?? "smoothstep",
    label: makeEdgeLabel(data),
    labelStyle: { fill: "#e2e8f0", fontSize: 11, fontWeight: 700 },
    labelBgStyle: { fill: "#111827", fillOpacity: 0.92 },
    labelBgPadding: [6, 4],
    labelBgBorderRadius: 6,
    markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: stroke },
    style: {
      ...(edge.style ?? {}),
      stroke,
      strokeWidth: type === "optional" ? 3 : 5,
      strokeDasharray: type === "optional" || type === "retargeting" || type === "wait" ? "8 6" : undefined,
    },
  };
}

function canvasEdgesFromGraph(graph: FunnelGraph): Edge[] {
  return graph.edges.map(e => decorateEdge({
    ...e,
    animated: true,
    type: "smoothstep",
    data: (e as unknown as { data?: CanvasEdgeData }).data ?? {
      edgeType: e.edgeType ?? "main",
      splitPercent: e.splitPercent ?? 100,
      edgeCvr: e.edgeCvr ?? e.conversionRate,
    },
  } as Edge));
}

function CanvasInner({ state, dispatch, content, locale, pendingSyncRef, flushRef }: CanvasInnerProps) {
  const { fitView, screenToFlowPosition, zoomIn, zoomOut } = useReactFlow();
  const [showStageMenu, setShowStageMenu] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [showKpiOverlay, setShowKpiOverlay] = useState(true);
  const [compactCanvas, setCompactCanvas] = useState(false);
  const [settledCanvasMode, setSettledCanvasMode] = useState<"compact" | "full" | null>(null);
  const [contextMenu, setContextMenu] = useState<CanvasContextMenu | null>(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [stageLinkCopyStatus, setStageLinkCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const canvasShellRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuPortal = typeof document !== "undefined" ? document.body : null;
  const canvasDirection = directionForFunnelLocale(locale);
  const canvasTextAlign = textAlignForFunnelLocale(locale);
  const toolbarPanelPosition = compactCanvas ? "top-center" : canvasDirection === "rtl" ? "top-right" : "top-left";
  const kpiPanelPosition = canvasDirection === "rtl" ? "top-left" : "top-right";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 720px)");
    const update = () => {
      const shell = document.body.getAttribute("data-device-shell");
      const shellWidth = canvasShellRef.current?.getBoundingClientRect().width ?? window.innerWidth;
      const compact = media.matches || shellWidth <= 520 || COMPACT_CANVAS_SHELLS.has(shell ?? "");
      setCompactCanvas(compact);
      if (compact) setShowKpiOverlay(false);
    };
    update();
    media.addEventListener("change", update);
    window.addEventListener("resize", update);
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: ["data-device-shell"] });
    const resizeObserver = typeof ResizeObserver !== "undefined" && canvasShellRef.current
      ? new ResizeObserver(update)
      : null;
    if (canvasShellRef.current) resizeObserver?.observe(canvasShellRef.current);
    return () => {
      media.removeEventListener("change", update);
      window.removeEventListener("resize", update);
      observer.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  const initialGraph = useMemo(
    () => state.graph ?? buildInitialGraph(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [nodes, updateNodes, applyNodeChanges] = useNodesState(
    initialGraph.nodes as Node[],
  );
  const [edges, updateEdges, applyEdgeChanges] = useEdgesState(canvasEdgesFromGraph(initialGraph));
  // Record permission at the actual edit, before the debounced state sync. Mount,
  // measurement, selection and parent reconciliation are not fresh planning edits.
  const setNodes = useCallback<typeof updateNodes>((value) => {
    noteDraftAction("SET_GRAPH");
    updateNodes(value);
  }, [updateNodes]);
  const setEdges = useCallback<typeof updateEdges>((value) => {
    noteDraftAction("SET_GRAPH");
    updateEdges(value);
  }, [updateEdges]);
  const onNodesChange = useCallback<typeof applyNodeChanges>((changes) => {
    if (changes.some((change) =>
      change.type === "position" ? change.dragging !== undefined
        : change.type === "dimensions" ? change.resizing !== undefined
          : change.type !== "select"
    )) noteDraftAction("SET_GRAPH");
    applyNodeChanges(changes);
  }, [applyNodeChanges]);
  const onEdgesChange = useCallback<typeof applyEdgeChanges>((changes) => {
    if (changes.some((change) => change.type !== "select")) noteDraftAction("SET_GRAPH");
    applyEdgeChanges(changes);
  }, [applyEdgeChanges]);
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const acknowledgedGraphRef = useRef(initialGraph);

  // ReactFlow keeps transient edits locally. Accept parent-owned edits (including
  // WebMCP) before paint, but do not echo our own debounced graph back into it.
  useLayoutEffect(() => {
    if (!state.graph || state.graph === acknowledgedGraphRef.current) return;
    if (syncTimer.current) {
      clearTimeout(syncTimer.current);
      syncTimer.current = null;
    }
    pendingSyncRef.current = false;
    acknowledgedGraphRef.current = state.graph;
    updateNodes(state.graph.nodes as Node[]);
    updateEdges(canvasEdgesFromGraph(state.graph));
  }, [state.graph, updateNodes, updateEdges, pendingSyncRef]);

  useEffect(() => {
    const canvasMode = compactCanvas ? "compact" : "full";
    let cancelled = false;
    const prefersReduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(
      () => {
        setSettledCanvasMode(null);
        void fitView({
          padding: compactCanvas ? 0.08 : 0.14,
          minZoom: compactCanvas ? 0.48 : 0.12,
          maxZoom: compactCanvas ? 0.9 : 0.9,
          duration: prefersReduced ? 0 : 300,
        }).then(() => {
          if (!cancelled) setSettledCanvasMode(canvasMode);
        });
      },
      compactCanvas ? 90 : 50,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [compactCanvas, fitView]);

  const fallbackVisitors = totalVisitorsFromChannels(
    state.trafficChannels,
    state.projectionInputs.monthlyVisitors,
  );

  const graphNodes = useMemo(
    () => nodes.map(n => n as unknown as FunnelGraphNode),
    [nodes],
  );
  const graphEdges = useMemo(
    () => edges.map(e => {
      const data = (e.data ?? {}) as CanvasEdgeData;
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        edgeType: data.edgeType ?? "main",
        splitPercent: data.splitPercent ?? 100,
        conversionRate: data.edgeCvr,
        edgeCvr: data.edgeCvr,
        waitDays: data.edgeType === "wait" ? 1 : 0,
        isRequired: !data.optional,
        label: typeof e.label === "string" ? e.label : undefined,
      };
    }) as FunnelGraphEdge[],
    [edges],
  );

  const volumes = useMemo(
    () => computeGraphVolumes(graphNodes, graphEdges, state.trafficChannels, fallbackVisitors),
    [graphNodes, graphEdges, state.trafficChannels, fallbackVisitors],
  );

  const canvasKpis = useMemo(() => {
    const stageNodes = graphNodes.filter((node) => node.type === "stage");
    const orderedStageNodes = [...stageNodes].sort((a, b) => a.position.x - b.position.x);
    const finalStage = orderedStageNodes[orderedStageNodes.length - 1] ?? null;
    const finalVolume = finalStage ? volumes.get(finalStage.id) ?? 0 : 0;
    const blendedCvr = fallbackVisitors > 0 ? (finalVolume / fallbackVisitors) * 100 : 0;
    const totalWaitDays = stageNodes.reduce((sum, node) => sum + (node.data.waitDays ?? 0), 0);
    return {
      visitors: fallbackVisitors,
      finalVolume,
      blendedCvr,
      stageCount: stageNodes.length,
      totalWaitDays,
    };
  }, [fallbackVisitors, graphNodes, volumes]);
  const hasStageMap = canvasKpis.stageCount > 0;
  const stageMapState = hasStageMap ? "complete_result" : "active_inprogress";
  const getCanvasViewportCenter = useCallback(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    const rect = canvasShellRef.current?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }
    return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  }, []);

  const openNodeMenuById = useCallback(
    (nodeId: string) => {
      const node = nodes.find((item) => item.id === nodeId);
      if (!node || typeof window === "undefined") return;
      const x = window.innerWidth / 2;
      const y = window.innerHeight - 16;
      setMenuPosition({ left: x, top: y });
      setContextMenu({ type: "node", x, y, nodeId });
      setShowSourceMenu(false);
      setShowStageMenu(false);
      track("funnel_planner_context_menu_action", {
        template_id: state.templateId ?? undefined,
        locale,
        action: "open",
        node_kind: node.type === "trafficSource" ? "traffic_source" : String((node.data as Record<string, unknown>).nodeKind ?? "stage"),
      });
    },
    [locale, nodes, state.templateId],
  );

  const enrichedNodes = useMemo(
    () =>
      nodes.map(n => ({
        ...n,
        data: {
          ...n.data,
          volume: volumes.get(n.id) ?? 0,
          ...(n.type === "stage"
            ? {
                content,
                locale,
                onOpenMenu: compactCanvas ? openNodeMenuById : undefined,
                onDelete: (nodeId: string) => {
                  setNodes(prev => prev.filter(x => x.id !== nodeId));
                  setEdges(prev =>
                    prev.filter(e => e.source !== nodeId && e.target !== nodeId),
                  );
                },
              }
            : { content, locale, onOpenMenu: compactCanvas ? openNodeMenuById : undefined }),
        },
      })),
    [nodes, volumes, compactCanvas, openNodeMenuById, setNodes, setEdges, content, locale],
  );

  const flushSync = useCallback<() => void>(() => {
    if (syncTimer.current) {
      clearTimeout(syncTimer.current);
      syncTimer.current = null;
    }
    pendingSyncRef.current = false;
    const graph: FunnelGraph = { nodes: graphNodes, edges: graphEdges };
    acknowledgedGraphRef.current = graph;
    // SET_GRAPH already derives stages atomically in the wizard reducer.
    dispatch({ type: "SET_GRAPH", graph, source: "automatic" });
  }, [graphNodes, graphEdges, dispatch, pendingSyncRef]);

  useLayoutEffect(() => {
    if (flushRef) flushRef.current = flushSync;
    return () => {
      if (flushRef?.current === flushSync) flushRef.current = null;
    };
  }, [flushRef, flushSync]);

  useEffect(() => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    pendingSyncRef.current = true;
    const timer = setTimeout(() => {
      syncTimer.current = null;
      pendingSyncRef.current = false;
      const graph: FunnelGraph = { nodes: graphNodes, edges: graphEdges };
      acknowledgedGraphRef.current = graph;
      dispatch({ type: "SET_GRAPH", graph, source: "automatic" });
    }, 600);
    syncTimer.current = timer;
    return () => {
      if (syncTimer.current === timer) {
        clearTimeout(timer);
        syncTimer.current = null;
        pendingSyncRef.current = false;
      }
    };
  }, [graphNodes, graphEdges, dispatch, pendingSyncRef]);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges(prev =>
        addEdge(
          decorateEdge({
            ...connection,
            id: `e-${connection.source}-${connection.target}-${Date.now()}`,
            animated: true,
            type: "smoothstep",
            data: { edgeType: "main", splitPercent: 100 },
          } as Edge),
          prev,
        ),
      );
    },
    [setEdges],
  );

  const handleAddStage = useCallback(
    (stageType: string, position?: FlowPoint) => {
      setShowStageMenu(false);
      setContextMenu(null);
      const id = `stage-custom-${Date.now()}`;
      const center = screenToFlowPosition(getCanvasViewportCenter());
      const target = position ?? { x: center.x - 100, y: center.y - 80 };
      const stageMeta = getCatalogStageTypeMeta(stageType);
      setNodes(prev => [
        ...prev,
        {
          id,
          type: "stage",
          position: target,
          data: {
            label: stageTypeLabel(content, stageType, stageMeta.label),
            stageType,
            conversionRate: stageMeta.defaultCvr,
            isSource: false,
            nodeKind: "stage",
            publishedStatus: "draft",
            trackingStatus: "planned",
            dueInDays: 14,
          },
        } as Node,
      ]);
    },
    [setNodes, screenToFlowPosition, getCanvasViewportCenter, content],
  );

  const handleAddSourceChannel = useCallback(
    (channel: string, position?: FlowPoint) => {
      setShowSourceMenu(false);
      setContextMenu(null);
      const opt = SOURCE_CHANNEL_OPTIONS.find(o => o.channel === channel);
      const id = `src-${channel}-${Date.now()}`;
      const existingSources = nodes.filter(n => n.type === "trafficSource");
      const lastY = existingSources.reduce((maxY, n) => Math.max(maxY, n.position.y), -160);
      const target = position ?? { x: 20, y: lastY + 160 };
      setNodes(prev => [
        ...prev,
        {
          id,
          type: "trafficSource",
          position: target,
          data: {
            label: opt ? channelLabel(content, opt.channel, opt.label) : funnelText(content, "canvasNewSourceLabel", "New Source"),
            channel,
            isSource: true,
            monthlyVisitors: 500,
          },
        } as Node,
      ]);
    },
    [nodes, setNodes, content],
  );

  const handleAddNodePreset = useCallback(
    (preset: NodePreset, position?: FlowPoint) => {
      setShowStageMenu(false);
      setContextMenu(null);
      const id = `stage-${preset.nodeKind}-${Date.now()}`;
      const center = screenToFlowPosition(getCanvasViewportCenter());
      const target = position ?? { x: center.x - 100, y: center.y - 80 };
      const stageMeta = getCatalogStageTypeMeta(preset.stageType);
      setNodes(prev => [
        ...prev,
        {
          id,
          type: "stage",
          position: target,
          data: {
            label: preset.label,
            stageType: preset.stageType,
            conversionRate: preset.conversionRate ?? stageMeta.defaultCvr,
            isSource: false,
            nodeKind: preset.nodeKind,
            waitDays: preset.waitDays,
            dueInDays: 14,
            optionalStage: preset.optionalStage ?? preset.nodeKind !== "stage",
            publishedStatus: "draft",
            trackingStatus: "planned",
          },
        } as Node,
      ]);
    },
    [screenToFlowPosition, getCanvasViewportCenter, setNodes],
  );

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      event.stopPropagation();
      triggerRef.current = event.target as HTMLElement;
      setMenuPosition({ left: event.clientX, top: event.clientY });
      setContextMenu({ type: "node", x: event.clientX, y: event.clientY, nodeId: node.id });
      track("funnel_planner_context_menu_action", {
        template_id: state.templateId ?? undefined,
        locale,
        action: "open",
        node_kind: node.type === "trafficSource" ? "traffic_source" : String((node.data as Record<string, unknown>).nodeKind ?? "stage"),
      });
    },
    [locale, state.templateId],
  );

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();
      event.stopPropagation();
      triggerRef.current = event.target as HTMLElement;
      setMenuPosition({ left: event.clientX, top: event.clientY });
      setContextMenu({ type: "edge", x: event.clientX, y: event.clientY, edgeId: edge.id });
      track("funnel_planner_context_menu_action", {
        template_id: state.templateId ?? undefined,
        locale,
        action: "open",
        edge_kind: String(((edge.data ?? {}) as CanvasEdgeData).edgeType ?? "main"),
      });
    },
    [locale, state.templateId],
  );

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!compactCanvas) return;
      event.stopPropagation();
      triggerRef.current = event.target as HTMLElement;
      const x = typeof window === "undefined" ? event.clientX : window.innerWidth / 2;
      const y = typeof window === "undefined" ? event.clientY : window.innerHeight - 16;
      setMenuPosition({ left: x, top: y });
      setContextMenu({ type: "node", x, y, nodeId: node.id });
      setShowSourceMenu(false);
      setShowStageMenu(false);
      track("funnel_planner_context_menu_action", {
        template_id: state.templateId ?? undefined,
        locale,
        action: "open",
        node_kind: node.type === "trafficSource" ? "traffic_source" : String((node.data as Record<string, unknown>).nodeKind ?? "stage"),
      });
    },
    [compactCanvas, locale, state.templateId],
  );

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (!compactCanvas) return;
      event.stopPropagation();
      triggerRef.current = event.target as HTMLElement;
      const x = typeof window === "undefined" ? event.clientX : window.innerWidth / 2;
      const y = typeof window === "undefined" ? event.clientY : window.innerHeight - 16;
      setMenuPosition({ left: x, top: y });
      setContextMenu({ type: "edge", x, y, edgeId: edge.id });
      setShowSourceMenu(false);
      setShowStageMenu(false);
      track("funnel_planner_context_menu_action", {
        template_id: state.templateId ?? undefined,
        locale,
        action: "open",
        edge_kind: String(((edge.data ?? {}) as CanvasEdgeData).edgeType ?? "main"),
      });
    },
    [compactCanvas, locale, state.templateId],
  );

  const onPaneContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent<Element>) => {
      event.preventDefault();
      event.stopPropagation();
      triggerRef.current = event.target as HTMLElement;
      setMenuPosition({ left: event.clientX, top: event.clientY });
      setContextMenu({
        type: "pane",
        x: event.clientX,
        y: event.clientY,
        flowPosition: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
      });
      track("funnel_planner_context_menu_action", {
        template_id: state.templateId ?? undefined,
        locale,
        action: "open",
      });
    },
    [locale, screenToFlowPosition, state.templateId],
  );

  const closeAllMenus = useCallback(() => {
    setContextMenu(null);
    setShowSourceMenu(false);
    setShowStageMenu(false);
    setStageLinkCopyStatus("idle");
    if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    if (!contextMenu || !contextMenuRef.current || typeof window === "undefined") return;
    const rect = contextMenuRef.current.getBoundingClientRect();
    const margin = 8;
    const left = Math.min(
      Math.max(margin, contextMenu.x),
      Math.max(margin, window.innerWidth - rect.width - margin),
    );
    const top = Math.min(
      Math.max(margin, contextMenu.y),
      Math.max(margin, window.innerHeight - rect.height - margin),
    );
    setMenuPosition({ left, top });
  }, [contextMenu]);

  useEffect(() => {
    if (contextMenu && contextMenuRef.current) {
      const first = contextMenuRef.current.querySelector<HTMLElement>("input, select, button");
      if (first) first.focus();
      else contextMenuRef.current.focus();
    }
  }, [contextMenu]);

  const updateNode = useCallback(
    (nodeId: string, delta: Record<string, unknown>) => {
      setNodes(prev =>
        prev.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...delta } } : n),
      );
    },
    [setNodes],
  );

  const updateEdge = useCallback(
    (edgeId: string, delta: CanvasEdgeData) => {
      setEdges(prev =>
        prev.map(edge => {
          if (edge.id !== edgeId) return edge;
          const next = {
            ...edge,
            data: { ...((edge.data ?? {}) as CanvasEdgeData), ...delta },
          } as Edge;
          return decorateEdge(next);
        }),
      );
    },
    [setEdges],
  );

  const deleteContextNode = useCallback(() => {
    if (!contextMenu || contextMenu.type !== "node") return;
    const id = contextMenu.nodeId;
    const node = nodes.find((item) => item.id === id);
    setNodes(prev => prev.filter(n => n.id !== id));
    setEdges(prev => prev.filter(e => e.source !== id && e.target !== id));
    setContextMenu(null);
    track("funnel_planner_context_menu_action", {
      template_id: state.templateId ?? undefined,
      locale,
      action: "delete_node",
      node_kind: node?.type === "trafficSource" ? "traffic_source" : String((node?.data as Record<string, unknown> | undefined)?.nodeKind ?? "stage"),
    });
  }, [contextMenu, locale, nodes, setNodes, setEdges, state.templateId]);

  const deleteContextEdge = useCallback(() => {
    if (!contextMenu || contextMenu.type !== "edge") return;
    const edge = edges.find((item) => item.id === contextMenu.edgeId);
    setEdges(prev => prev.filter(edge => edge.id !== contextMenu.edgeId));
    setContextMenu(null);
    track("funnel_planner_context_menu_action", {
      template_id: state.templateId ?? undefined,
      locale,
      action: "delete_edge",
      edge_kind: String(((edge?.data ?? {}) as CanvasEdgeData).edgeType ?? "main"),
    });
  }, [contextMenu, edges, locale, setEdges, state.templateId]);

  const addPaneStage = useCallback(() => {
    if (!contextMenu || contextMenu.type !== "pane") return;
    handleAddStage("landing-page", contextMenu.flowPosition);
    track("funnel_planner_context_menu_action", {
      template_id: state.templateId ?? undefined,
      locale,
      action: "add_stage",
      node_kind: "stage",
    });
  }, [contextMenu, handleAddStage, locale, state.templateId]);

  const addPaneSource = useCallback(() => {
    if (!contextMenu || contextMenu.type !== "pane") return;
    handleAddSourceChannel("custom", contextMenu.flowPosition);
    track("funnel_planner_context_menu_action", {
      template_id: state.templateId ?? undefined,
      locale,
      action: "add_source",
      node_kind: "traffic_source",
    });
  }, [contextMenu, handleAddSourceChannel, locale, state.templateId]);

  const addPanePreset = useCallback((preset: NodePreset) => {
    if (!contextMenu || contextMenu.type !== "pane") return;
    handleAddNodePreset(preset, contextMenu.flowPosition);
    const action =
      preset.nodeKind === "branch" ? "add_branch"
      : preset.nodeKind === "retargeting" ? "add_retargeting"
      : preset.nodeKind === "wait" ? "add_wait"
      : preset.nodeKind === "survey" ? "add_survey"
      : "add_stage";
    track("funnel_planner_context_menu_action", {
      template_id: state.templateId ?? undefined,
      locale,
      action,
      node_kind: preset.nodeKind,
    });
    if (preset.nodeKind === "branch" || preset.nodeKind === "retargeting" || preset.nodeKind === "wait" || preset.nodeKind === "survey") {
      track("funnel_planner_branch_node_added", {
        template_id: state.templateId ?? undefined,
        locale,
        action,
        node_kind: preset.nodeKind,
      });
    }
  }, [contextMenu, handleAddNodePreset, locale, state.templateId]);

  const copyContextStageLink = useCallback(async () => {
    if (!contextMenu || contextMenu.type !== "node") return;
    const node = nodes.find((item) => item.id === contextMenu.nodeId);
    if (!node || node.type !== "stage") return;
    const shareState: FunnelWizardState = {
      ...state,
      step: 5,
      graph: { nodes: graphNodes, edges: graphEdges },
      stages: graphToStages(graphNodes, graphEdges),
    };
    const copied = await copyCanvasTextToClipboard(buildPlannerStageShareUrl(shareState, contextMenu.nodeId));
    setStageLinkCopyStatus(copied ? "copied" : "failed");
    track("funnel_planner_share_stage_link_copied", {
      template_id: state.templateId ?? undefined,
      locale,
      copied,
    });
    track("funnel_planner_context_menu_action", {
      template_id: state.templateId ?? undefined,
      locale,
      action: "copy_stage_link",
      node_kind: String((node.data as Record<string, unknown>).nodeKind ?? "stage"),
    });
    window.setTimeout(() => setStageLinkCopyStatus("idle"), 1800);
  }, [contextMenu, graphEdges, graphNodes, locale, nodes, state]);

  const clearSelection = useCallback(() => {
    setNodes(prev => prev.map(node => ({ ...node, selected: false })));
    setEdges(prev => prev.map(edge => ({ ...edge, selected: false })));
    setContextMenu(null);
  }, [setNodes, setEdges]);

  const fitCanvasView = useCallback(() => {
    fitView({
      padding: compactCanvas ? 0.08 : 0.16,
      minZoom: compactCanvas ? 0.48 : 0.12,
      maxZoom: compactCanvas ? 1 : 1.1,
      duration: 220,
    });
    setContextMenu(null);
  }, [compactCanvas, fitView]);

  const contextNode = contextMenu?.type === "node" ? nodes.find(n => n.id === contextMenu.nodeId) : null;
  const contextEdge = contextMenu?.type === "edge" ? edges.find(e => e.id === contextMenu.edgeId) : null;
  const kpiOverlayItems = [
    [funnelText(content, "canvasOverlayVisitors", "Visitors"), formatFunnelNumber(canvasKpis.visitors, locale)],
    [funnelText(content, "canvasOverlayFinalStage", "Final stage"), formatFunnelNumber(canvasKpis.finalVolume, locale)],
    [funnelText(content, "canvasOverlayBlendedCvr", "Blended CVR"), formatFunnelPercent(canvasKpis.blendedCvr, locale)],
    [funnelText(content, "canvasOverlayStages", "Stages"), formatFunnelNumber(canvasKpis.stageCount, locale)],
    [funnelText(content, "canvasOverlayWaitDays", "Wait days"), formatFunnelNumber(canvasKpis.totalWaitDays, locale)],
  ];

  return (
    <div
      ref={canvasShellRef}
      dir="ltr"
      onContextMenuCapture={(event) => {
        event.preventDefault();
      }}
      data-funnel-planner-funnel-stage-map="true"
      data-funnel-canvas-shell="true"
      data-funnel-canvas-compact={compactCanvas ? "true" : undefined}
      data-funnel-canvas-fit-ready={settledCanvasMode === (compactCanvas ? "compact" : "full") ? "true" : "false"}
      data-funnel-planner-direction={canvasDirection}
      className="funnel-canvas-shell"
      data-audit-ticket={FUNNEL_STAGE_MAP_AUDIT_TICKETS}
      data-tier-one-family="readiness_board"
      data-gamification-current-state={stageMapState}
      data-gamification-reduced-motion-static="supported"
      data-gamification-supported-states={READINESS_SUPPORTED_STATES}
      data-readiness-ready-count={hasStageMap ? 1 : 0}
      data-readiness-total-count={1}
      data-readiness-tier={hasStageMap ? "stage_map_ready" : "stage_map_needed"}
      data-blockers-bucket={hasStageMap ? "none" : "one"}
      style={{
        width: "100%",
        height: "var(--fp-canvas-height, calc(100vh - 260px))",
        minHeight: "var(--fp-canvas-min-height, 500px)",
        position: "relative",
        background: "var(--color-canvas-bg, #0f172a)",
        borderRadius: 12,
        border: "1px solid var(--color-panel, #1e293b)",
        direction: "ltr",
        textAlign: "left",
        "--funnel-planner-text-align": canvasTextAlign,
        "--funnel-planner-direction": canvasDirection,
      } as React.CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string>}
    >
      <ReactFlow
        nodes={enrichedNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeContextMenu={onEdgeContextMenu}
        onPaneContextMenu={onPaneContextMenu}
        onPaneClick={closeAllMenus}
        nodeTypes={NODE_TYPES}
        colorMode="dark"
        fitView={false}
        deleteKeyCode={["Backspace", "Delete"]}
        multiSelectionKeyCode="Shift"
        maxZoom={2}
        minZoom={compactCanvas ? 0.16 : 0.08}
        style={{ direction: "ltr" }}
        defaultEdgeOptions={{
          animated: true,
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: "var(--color-accent-edge, #818cf8)" },
          style: { stroke: "var(--color-accent-edge, #818cf8)", strokeWidth: 5 },
        }}
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="var(--color-border-subtle, #334155)" />
        {!compactCanvas ? (
          <>
            <Controls style={{ background: "var(--color-panel, #1e293b)", border: "1px solid var(--color-border-subtle, #334155)", borderRadius: 8 }} />
            <MiniMap
              style={{ background: "var(--color-panel, #1e293b)", border: "1px solid var(--color-border-subtle, #334155)", borderRadius: 8 }}
              nodeColor={n => {
                const data = n.data as Record<string, unknown>;
                const customColor = normalizeNodeColor(data.color);
                if (customColor) return customColor;
                if (n.type === "trafficSource") return sourceChannelColor(data.channel);
                return stageColor(typeof data.stageType === "string" ? data.stageType : undefined);
              }}
            />
          </>
        ) : (
          <Panel position="bottom-left">
            <div
              data-funnel-canvas-mobile-controls="true"
              style={{
                display: "grid",
                gap: 6,
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.28)",
                background: "rgba(15,23,42,0.9)",
                boxShadow: "0 12px 30px rgba(0,0,0,0.3)",
                padding: 5,
              }}
            >
              {[
                { label: funnelText(content, "canvasZoomInButton", "Zoom in"), text: "+", action: () => zoomIn({ duration: 150 }) },
                { label: funnelText(content, "canvasZoomOutButton", "Zoom out"), text: "-", action: () => zoomOut({ duration: 150 }) },
                { label: funnelText(content, "canvasFitViewButton", "Fit view"), text: "[]", action: fitCanvasView },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  aria-label={item.label}
                  onClick={item.action}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: "1px solid rgba(148,163,184,0.22)",
                    background: "rgba(30,41,59,0.92)",
                    color: "var(--color-text, #e2e8f0)",
                    fontSize: item.text === "[]" ? 12 : 20,
                    fontWeight: 750,
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  {item.text}
                </button>
              ))}
            </div>
          </Panel>
        )}

        {/* Toolbar */}
        <Panel position={toolbarPanelPosition}>
          <div
            dir={canvasDirection}
            data-funnel-planner-stage-conversion-controls="true"
            data-audit-ticket={STAGE_CONVERSION_CONTROLS_AUDIT_TICKETS}
            data-tier-one-family="readiness_board"
            data-gamification-current-state={stageMapState}
            data-gamification-reduced-motion-static="supported"
            data-gamification-supported-states={READINESS_SUPPORTED_STATES}
            data-readiness-ready-count={hasStageMap ? 1 : 0}
            data-readiness-total-count={1}
            data-readiness-tier={hasStageMap ? "stage_controls_ready" : "stage_controls_needed"}
            data-blockers-bucket={hasStageMap ? "none" : "one"}
            style={{
              display: compactCanvas ? "grid" : "flex",
              alignItems: "center",
              gridTemplateColumns: compactCanvas ? "minmax(0, 1fr) minmax(0, 1fr) auto auto" : undefined,
              gap: compactCanvas ? 6 : 8,
              background: compactCanvas ? "rgba(15,23,42,0.94)" : "var(--color-panel, #1e293b)",
              border: "1px solid var(--color-border-subtle, #334155)",
              borderRadius: compactCanvas ? 10 : 8,
              padding: compactCanvas ? 6 : "6px 10px",
              width: compactCanvas ? "min(calc(100vw - 1rem), 26rem)" : undefined,
              maxWidth: compactCanvas ? "calc(100vw - 1rem)" : undefined,
              boxShadow: compactCanvas ? "0 14px 34px rgba(0,0,0,0.32)" : undefined,
              direction: canvasDirection,
              textAlign: canvasTextAlign,
            }}
          >
            <span className="sr-only" role="status" aria-live="polite">
              {hasStageMap
                ? funnelText(content, "gamificationStageMapReady", "Funnel stage map ready")
                : funnelText(content, "gamificationStageMapWaiting", "Funnel stage map waiting for a stage")}
            </span>
            {!compactCanvas && (
              <>
                <span style={{ fontSize: 11, color: "var(--color-muted, #cbd5e1)", fontWeight: 500 }}>
                  {funnelText(content, "canvasToolbarTitle", "Funnel Canvas")}
                </span>
                <div style={{ width: 1, height: 14, background: "var(--color-border-subtle, #334155)" }} />
              </>
            )}

            {/* + Source picker */}
            <div style={{ position: "relative", order: compactCanvas ? 2 : undefined, minWidth: 0 }}>
              <button
                type="button"
                {...stepFiveOfferAssemblyCtaAttributes(stepFiveOfferAssemblyCtaTicketsForAction(0, 3))}
                aria-label={funnelText(content, "canvasAddSourceAria", "Add traffic source")}
                aria-haspopup="listbox"
                aria-expanded={showSourceMenu}
                aria-controls="source-channel-menu"
                onClick={() => { setShowSourceMenu(v => !v); setShowStageMenu(false); setShowKpiOverlay(false); }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                  width: compactCanvas ? "100%" : undefined,
                  padding: compactCanvas ? "8px 10px" : "8px 12px", borderRadius: 6,
                  background: "var(--color-accent-source, #1e40af)", border: "none", color: "white",
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                  minHeight: 36,
                  whiteSpace: "nowrap",
                }}
                onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-primary, #2563eb)"; e.currentTarget.style.outlineOffset = "2px"; }}
                onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
              >
                <span className="sr-only" role="status" aria-live="polite">
                  {funnelText(content, "gamificationOfferAssemblyCtaEmptyStatus", "Mark this planner action for the implementation brief.")}
                </span>
                <span style={{ fontSize: 13, lineHeight: 1 }}>+</span>
                {funnelText(content, "canvasSourceButton", "Source")}
                <span style={{ fontSize: 9, opacity: 0.7, marginInlineStart: 1 }} aria-hidden="true">v</span>
              </button>

              {showSourceMenu && (
                <div
                  id="source-channel-menu"
                  role="listbox"
                  aria-label={funnelText(content, "canvasChooseChannelLabel", "Choose traffic channel")}
                  dir={canvasDirection}
                  style={{
                    position: compactCanvas ? "fixed" : "absolute",
                    top: compactCanvas ? "auto" : "calc(100% + 6px)",
                    insetInlineStart: compactCanvas ? "auto" : 0,
                    left: compactCanvas ? 8 : undefined,
                    right: compactCanvas ? 8 : undefined,
                    bottom: compactCanvas ? 8 : undefined,
                    zIndex: compactCanvas ? 10000 : 999,
                    background: "var(--color-panel, #1e293b)", border: "1px solid var(--color-border-subtle, #334155)", borderRadius: 10,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                    padding: "6px 0", minWidth: compactCanvas ? "auto" : 210, maxHeight: compactCanvas ? "min(64dvh, 28rem)" : 400, overflowY: "auto",
                    direction: canvasDirection,
                    textAlign: canvasTextAlign,
                  }}
                >
                  <div style={{ padding: "3px 14px 6px", fontSize: 11, fontWeight: 700, color: "var(--color-muted, #cbd5e1)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {funnelText(content, "canvasChooseChannelLabel", "Choose traffic channel")}
                  </div>
                  {SOURCE_CHANNEL_OPTIONS.map(opt => (
                    <button
                      key={opt.channel}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => handleAddSourceChannel(opt.channel)}
                      style={{
                        display: "flex", alignItems: "center", gap: 9,
                        width: "100%", padding: "6px 14px",
                        background: "transparent", border: "none",
                        cursor: "pointer", fontSize: 12, color: "var(--color-text, #e2e8f0)", textAlign: canvasTextAlign,
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-border-subtle, #334155)"; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                      onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-primary, #2563eb)"; e.currentTarget.style.outlineOffset = "-2px"; }}
                      onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 24, height: 24, borderRadius: 5,
                          background: opt.color,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 9, fontWeight: 700, color: "white",
                          flexShrink: 0, letterSpacing: "-0.5px",
                        }}
                      >
                        {opt.icon}
                      </span>
                      {channelLabel(content, opt.channel, opt.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!compactCanvas && <div style={{ width: 1, height: 14, background: "var(--color-border-subtle, #334155)" }} />}

            {compactCanvas && (
              <button
                type="button"
                aria-label={funnelText(content, "canvasFitViewButton", "Fit view")}
                onClick={fitCanvasView}
                style={{
                  order: 3,
                  width: 40,
                  minWidth: 40,
                  minHeight: 36,
                  borderRadius: 7,
                  border: "1px solid var(--color-border-subtle, #334155)",
                  background: "rgba(30,41,59,0.92)",
                  color: "var(--color-text, #e2e8f0)",
                  fontSize: 11,
                  fontWeight: 750,
                  cursor: "pointer",
                }}
              >
                Fit
              </button>
            )}

            <button
              type="button"
              {...stepFiveOfferAssemblyCtaAttributes(stepFiveOfferAssemblyCtaTicketsForAction(1, 3))}
              aria-pressed={showKpiOverlay}
              onClick={() => { setShowKpiOverlay((value) => !value); setShowSourceMenu(false); setShowStageMenu(false); }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
                order: compactCanvas ? 4 : undefined,
                width: compactCanvas ? 48 : undefined,
                minWidth: compactCanvas ? 48 : undefined,
                padding: compactCanvas ? "8px 7px" : "8px 10px",
                borderRadius: 6,
                background: showKpiOverlay ? "rgba(37,99,235,0.22)" : "transparent",
                border: "1px solid var(--color-border-subtle, #334155)",
                color: "var(--color-text, #e2e8f0)",
                fontSize: 11,
                fontWeight: 650,
                cursor: "pointer",
                minHeight: 36,
                whiteSpace: "nowrap",
              }}
              onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-primary, #2563eb)"; e.currentTarget.style.outlineOffset = "2px"; }}
              onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
            >
              <span className="sr-only" role="status" aria-live="polite">
                {funnelText(content, "gamificationOfferAssemblyCtaEmptyStatus", "Mark this planner action for the implementation brief.")}
              </span>
              {compactCanvas
                ? "KPIs"
                : showKpiOverlay
                  ? funnelText(content, "canvasHideKpiOverlayButton", "Hide KPIs")
                  : funnelText(content, "canvasShowKpiOverlayButton", "Show KPIs")}
            </button>

            {/* + Stage picker */}
            <div style={{ position: "relative", order: compactCanvas ? 1 : undefined, minWidth: 0 }}>
              <button
                type="button"
                {...stepFiveOfferAssemblyCtaAttributes(stepFiveOfferAssemblyCtaTicketsForAction(2, 3))}
                aria-label={funnelText(content, "canvasAddStageAria", "Add funnel stage")}
                aria-haspopup="listbox"
                aria-expanded={showStageMenu}
                aria-controls="stage-type-menu"
                onClick={() => { setShowStageMenu(v => !v); setShowSourceMenu(false); setShowKpiOverlay(false); }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                  width: compactCanvas ? "100%" : undefined,
                  padding: compactCanvas ? "8px 10px" : "8px 12px", borderRadius: 6,
                  background: "var(--color-accent-stage, #6366f1)", border: "none", color: "white",
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                  minHeight: 36,
                  whiteSpace: "nowrap",
                }}
                onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-primary, #2563eb)"; e.currentTarget.style.outlineOffset = "2px"; }}
                onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
              >
                <span className="sr-only" role="status" aria-live="polite">
                  {funnelText(content, "gamificationOfferAssemblyCtaEmptyStatus", "Mark this planner action for the implementation brief.")}
                </span>
                <span style={{ fontSize: 13, lineHeight: 1 }}>+</span>
                {funnelText(content, "canvasStageButton", "Stage")}
                <span style={{ fontSize: 9, opacity: 0.7, marginInlineStart: 1 }} aria-hidden="true">v</span>
              </button>

              {showStageMenu && (
                <StageTypePicker
                  value="landing-page"
                  onChange={handleAddStage}
                  onClose={() => setShowStageMenu(false)}
                  content={content}
                  compact={compactCanvas}
                  recommendedTypes={DEFAULT_RECOMMENDED_STAGE_TYPES}
                />
              )}
            </div>
          </div>
        </Panel>

        {!compactCanvas && (
          <Panel position="bottom-center">
            <div
              dir={canvasDirection}
              style={{
                fontSize: 10, color: "var(--color-muted-dim, #64748b)",
                background: "var(--color-panel-alpha, #1e293b99)", backdropFilter: "blur(4px)",
                borderRadius: 6, padding: "4px 10px", marginBottom: 48,
                direction: canvasDirection,
                textAlign: canvasTextAlign,
              }}
            >
              {funnelText(content, "canvasHint", "Drag handles to connect - Drag nodes to reposition - Scroll to zoom - Right-click any node to edit")}
            </div>
          </Panel>
        )}

        {showKpiOverlay && !compactCanvas && (
          <Panel position={kpiPanelPosition}>
            <section
              dir={canvasDirection}
              aria-label={funnelText(content, "canvasKpiOverlayTitle", "Canvas KPI overlay")}
              data-funnel-canvas-kpi-overlay
              style={{
                width: compactCanvas ? "min(calc(100vw - 48px), 340px)" : 260,
                background: "rgba(15,23,42,0.92)",
                border: "1px solid var(--color-border-subtle, #334155)",
                borderRadius: 10,
                boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
                padding: compactCanvas ? 10 : 12,
                color: "var(--color-text, #e2e8f0)",
                backdropFilter: "blur(8px)",
                direction: canvasDirection,
                textAlign: canvasTextAlign,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 800, color: "var(--color-muted, #cbd5e1)", textTransform: "uppercase", letterSpacing: 0, marginBottom: 8 }}>
                {funnelText(content, "canvasKpiOverlayTitle", "Canvas KPI overlay")}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {kpiOverlayItems.map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: 8,
                      border: "1px solid rgba(148,163,184,0.18)",
                      background: "rgba(30,41,59,0.7)",
                      padding: "7px 8px",
                    }}
                  >
                    <div style={{ fontSize: 10, color: "var(--color-muted, #cbd5e1)", marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                  </div>
                ))}
              </div>
            </section>
          </Panel>
        )}
      </ReactFlow>

      {compactCanvas && showKpiOverlay && menuPortal && createPortal((
        <>
          <div
            aria-hidden="true"
            style={{ position: "fixed", inset: 0, zIndex: 9996, background: "rgba(2,6,23,0.42)" }}
            onClick={() => setShowKpiOverlay(false)}
          />
          <section
            dir={canvasDirection}
            aria-label={funnelText(content, "canvasKpiOverlayTitle", "Canvas KPI overlay")}
            data-funnel-canvas-kpi-overlay
            data-funnel-canvas-mobile-sheet="kpi"
            style={{
              position: "fixed",
              left: 8,
              right: 8,
              bottom: 8,
              zIndex: 9997,
              borderRadius: 12,
              border: "1px solid var(--color-border-subtle, #334155)",
              background: "rgba(15,23,42,0.98)",
              boxShadow: "0 18px 50px rgba(0,0,0,0.5)",
              padding: 12,
              color: "var(--color-text, #e2e8f0)",
              direction: canvasDirection,
              textAlign: canvasTextAlign,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "var(--color-muted, #cbd5e1)", textTransform: "uppercase", letterSpacing: 0 }}>
                {funnelText(content, "canvasKpiOverlayTitle", "Canvas KPI overlay")}
              </div>
              <button
                type="button"
                aria-label={funnelText(content, "canvasHideKpiOverlayButton", "Hide KPIs")}
                onClick={() => setShowKpiOverlay(false)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(30,41,59,0.9)",
                  color: "var(--color-text, #e2e8f0)",
                  fontSize: 16,
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                x
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
              {kpiOverlayItems.map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    borderRadius: 8,
                    border: "1px solid rgba(148,163,184,0.18)",
                    background: "rgba(30,41,59,0.75)",
                    padding: "8px 9px",
                    minWidth: 0,
                  }}
                >
                  <div style={{ fontSize: 10, color: "var(--color-muted, #cbd5e1)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                </div>
              ))}
            </div>
          </section>
        </>
      ), menuPortal)}

      {nodes.filter(n => n.type === "stage").length === 0 && (
        <p
          role="alert"
          style={{
            position: "absolute",
            bottom: compactCanvas ? 86 : 60,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 0,
            padding: "6px 14px",
            borderRadius: 6,
            background: "var(--color-panel, #1e293b)",
            border: "1px solid var(--color-border-subtle, #334155)",
            fontSize: 12,
            color: "var(--color-muted, #cbd5e1)",
            pointerEvents: "none",
            whiteSpace: compactCanvas ? "normal" : "nowrap",
            width: compactCanvas ? "min(calc(100% - 5.5rem), 18rem)" : undefined,
            textAlign: "center",
            zIndex: 10,
          }}
        >
          {funnelText(content, "canvasNoStageAlert", "Add at least one stage to continue")}
        </p>
      )}

      {menuPortal && contextMenu?.type === "pane" && createPortal((
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9998 }}
            onClick={closeAllMenus}
            onContextMenu={(event) => {
              event.preventDefault();
              closeAllMenus();
            }}
          />
          <div
            ref={contextMenuRef}
            role="menu"
            aria-label={funnelText(content, "canvasPlannerMenuTitle", "Funnel Planner menu")}
            data-funnel-canvas-menu="pane"
            dir={canvasDirection}
            style={{
              position: "fixed",
              left: compactCanvas ? 8 : menuPosition.left,
              right: compactCanvas ? 8 : undefined,
              top: compactCanvas ? "auto" : menuPosition.top,
              bottom: compactCanvas ? 8 : undefined,
              zIndex: 9999,
              background: "var(--color-panel, #1e293b)",
              border: "1px solid var(--color-border-mid, #475569)",
              borderRadius: 10,
              boxShadow: "0 12px 40px rgba(0,0,0,0.65)",
              padding: 14,
              width: compactCanvas ? "auto" : 220,
              maxHeight: compactCanvas ? "min(72dvh, 34rem)" : undefined,
              overflowY: compactCanvas ? "auto" : undefined,
              direction: canvasDirection,
              textAlign: canvasTextAlign,
              "--funnel-planner-text-align": canvasTextAlign,
              "--funnel-planner-direction": canvasDirection,
            } as React.CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string>}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onContextMenu={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-muted, #cbd5e1)", textTransform: "uppercase", letterSpacing: 0 }}>
                {funnelText(content, "canvasPlannerMenuTitle", "Funnel Planner")}
              </span>
              <button
                type="button"
                onClick={closeAllMenus}
                style={{ background: "none", border: "none", color: "var(--color-muted-dim, #64748b)", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}
              >
                x
              </button>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={addPaneStage}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(59,130,246,0.16)"; (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasAddStageHere", "Add funnel stage here")}
              <span aria-hidden="true">+</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={addPaneSource}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(59,130,246,0.16)"; (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasAddSourceHere", "Add traffic source here")}
              <span aria-hidden="true">+</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => addPanePreset({ label: "Branch / split", stageType: "quiz-page", nodeKind: "branch", conversionRate: 50, optionalStage: true })}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(59,130,246,0.16)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasAddBranchHere", "Add branch split here")}
              <span aria-hidden="true">Y/N</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => addPanePreset({ label: "Retargeting loop", stageType: "thank-you-page", nodeKind: "retargeting", conversionRate: 18, optionalStage: true })}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(59,130,246,0.16)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasAddRetargetingHere", "Add retargeting step")}
              <span aria-hidden="true">RT</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => addPanePreset({ label: "Wait / delay", stageType: "custom", nodeKind: "wait", conversionRate: 100, waitDays: 3, optionalStage: true })}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(59,130,246,0.16)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasAddWaitHere", "Add wait/delay step")}
              <span aria-hidden="true">3d</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => addPanePreset({ label: "Survey / quiz split", stageType: "application-page", nodeKind: "survey", conversionRate: 20, optionalStage: true })}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(59,130,246,0.16)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasAddSurveyHere", "Add survey/quiz split")}
              <span aria-hidden="true">%</span>
            </button>
            <div style={{ height: 1, background: "var(--color-border-subtle, #334155)", margin: "8px 0" }} />
            <button
              type="button"
              role="menuitem"
              onClick={fitCanvasView}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(59,130,246,0.16)"; (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasFitViewButton", "Fit view")}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={clearSelection}
              style={CTX_MENU_BUTTON}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(59,130,246,0.16)"; (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(96,165,250,0.35)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent"; }}
            >
              {funnelText(content, "canvasClearSelectionButton", "Clear selection")}
            </button>
          </div>
        </>
      ), menuPortal)}

      {menuPortal && contextMenu?.type === "edge" && contextEdge && createPortal((
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9998 }}
            onClick={closeAllMenus}
            onContextMenu={(event) => {
              event.preventDefault();
              closeAllMenus();
            }}
          />
          <div
            ref={contextMenuRef}
            role="menu"
            aria-label={funnelText(content, "canvasEdgeMenuTitle", "Funnel connector")}
            data-funnel-canvas-menu="edge"
            dir={canvasDirection}
            style={{
              position: "fixed",
              left: compactCanvas ? 8 : menuPosition.left,
              right: compactCanvas ? 8 : undefined,
              top: compactCanvas ? "auto" : menuPosition.top,
              bottom: compactCanvas ? 8 : undefined,
              zIndex: 9999,
              background: "var(--color-panel, #1e293b)",
              border: "1px solid var(--color-border-mid, #475569)",
              borderRadius: 10,
              boxShadow: "0 12px 40px rgba(0,0,0,0.65)",
              padding: 14,
              width: compactCanvas ? "auto" : 280,
              maxHeight: compactCanvas ? "min(72dvh, 34rem)" : undefined,
              overflowY: compactCanvas ? "auto" : undefined,
              direction: canvasDirection,
              textAlign: canvasTextAlign,
              "--funnel-planner-text-align": canvasTextAlign,
              "--funnel-planner-direction": canvasDirection,
            } as React.CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string>}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onContextMenu={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-muted, #cbd5e1)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {funnelText(content, "canvasEdgeMenuTitle", "Funnel connector")}
              </span>
              <button
                type="button"
                onClick={closeAllMenus}
                style={{ background: "none", border: "none", color: "var(--color-muted-dim, #64748b)", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}
              >
                x
              </button>
            </div>

            <label style={CTX_LABEL}>{funnelText(content, "canvasEdgeTypeField", "Connector type")}</label>
            <select
              key={`edge-type-${contextMenu.edgeId}`}
              defaultValue={String(((contextEdge.data ?? {}) as CanvasEdgeData).edgeType ?? "main")}
              onChange={e => updateEdge(contextMenu.edgeId, { edgeType: e.target.value })}
              onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
              onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
              style={CTX_SELECT}
            >
              {EDGE_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={CTX_LABEL}>{funnelText(content, "canvasEdgeSplitField", "Split (%)")}</label>
                <input
                  key={`edge-split-${contextMenu.edgeId}`}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  defaultValue={Number(((contextEdge.data ?? {}) as CanvasEdgeData).splitPercent ?? 100)}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0 && v <= 100) updateEdge(contextMenu.edgeId, { splitPercent: v }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
              </div>
              <div>
                <label style={CTX_LABEL}>{funnelText(content, "canvasEdgeCvrField", "Edge CVR (%)")}</label>
                <input
                  key={`edge-cvr-${contextMenu.edgeId}`}
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  defaultValue={Number(((contextEdge.data ?? {}) as CanvasEdgeData).edgeCvr ?? 100)}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0 && v <= 100) updateEdge(contextMenu.edgeId, { edgeCvr: v }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
              </div>
            </div>

            <label style={CTX_CHECKBOX_ROW}>
              <input
                key={`edge-optional-${contextMenu.edgeId}`}
                type="checkbox"
                defaultChecked={Boolean(((contextEdge.data ?? {}) as CanvasEdgeData).optional)}
                onChange={e => updateEdge(contextMenu.edgeId, { optional: e.target.checked, edgeType: e.target.checked ? "optional" : ((contextEdge.data ?? {}) as CanvasEdgeData).edgeType ?? "main" })}
              />
              {funnelText(content, "canvasEdgeOptionalField", "Optional path")}
            </label>

            <div style={{ height: 1, background: "var(--color-border-subtle, #334155)", margin: "4px 0 10px" }} />
            <button
              type="button"
              onClick={deleteContextEdge}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                width: "100%", padding: "6px 10px",
                borderRadius: 6, background: "transparent",
                border: "1px solid var(--color-error, #ef4444)", color: "var(--color-error, #ef4444)",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.09)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              {funnelText(content, "canvasDeleteEdgeButton", "Delete connector")}
            </button>
          </div>
        </>
      ), menuPortal)}

      {/* Right-click context menu */}
      {menuPortal && contextMenu?.type === "node" && contextNode && createPortal((
        <>
          {/* Dismiss overlay */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9998 }}
            onClick={closeAllMenus}
            onContextMenu={(event) => {
              event.preventDefault();
              closeAllMenus();
            }}
          />
          {/* Menu panel */}
          <div
            ref={contextMenuRef}
            role="menu"
            aria-label={contextNode.type === "trafficSource"
              ? funnelText(content, "canvasTrafficSourceTitle", "Traffic Source")
              : funnelText(content, "canvasFunnelStageTitle", "Funnel Stage")}
            data-funnel-canvas-menu="node"
            dir={canvasDirection}
            style={{
              position: "fixed",
              left: compactCanvas ? 8 : menuPosition.left,
              right: compactCanvas ? 8 : undefined,
              top: compactCanvas ? "auto" : menuPosition.top,
              bottom: compactCanvas ? 8 : undefined,
              zIndex: 9999,
              background: "var(--color-panel, #1e293b)",
              border: "1px solid var(--color-border-mid, #475569)",
              borderRadius: 10,
              boxShadow: "0 12px 40px rgba(0,0,0,0.65)",
              padding: 14,
              width: compactCanvas ? "auto" : 320,
              maxHeight: compactCanvas ? "min(72dvh, 34rem)" : "min(78vh, 760px)",
              overflowY: "auto",
              direction: canvasDirection,
              textAlign: canvasTextAlign,
              "--funnel-planner-text-align": canvasTextAlign,
              "--funnel-planner-direction": canvasDirection,
            } as React.CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string>}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onContextMenu={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-muted, #cbd5e1)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {contextNode.type === "trafficSource"
                  ? funnelText(content, "canvasTrafficSourceTitle", "Traffic Source")
                  : funnelText(content, "canvasFunnelStageTitle", "Funnel Stage")}
              </span>
              <button
                type="button"
                onClick={closeAllMenus}
                style={{ background: "none", border: "none", color: "var(--color-muted-dim, #64748b)", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}
              >
                x
              </button>
            </div>

            {contextNode.type === "trafficSource" ? (
              <>
                <label style={CTX_LABEL}>{funnelText(content, "canvasLabelField", "Label")}</label>
                <input
                  key={`label-${contextMenu.nodeId}`}
                  defaultValue={String((contextNode.data as Record<string, unknown>).label ?? "")}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; const v = e.target.value.trim(); if (v) updateNode(contextMenu.nodeId, { label: v }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
                <label style={CTX_LABEL}>{funnelText(content, "canvasVisitorsField", "Visitors / mo")}</label>
                <input
                  key={`vis-${contextMenu.nodeId}`}
                  type="number"
                  min={0}
                  defaultValue={Number((contextNode.data as Record<string, unknown>).monthlyVisitors ?? 500)}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 0) updateNode(contextMenu.nodeId, { monthlyVisitors: v }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
                <label style={CTX_LABEL}>{funnelText(content, "canvasChannelTypeField", "Channel type")}</label>
                <select
                  key={`ch-${contextMenu.nodeId}`}
                  defaultValue={String((contextNode.data as Record<string, unknown>).channel ?? "custom")}
                  onChange={e => updateNode(contextMenu.nodeId, { channel: e.target.value })}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
                  style={CTX_SELECT}
                >
                  {SOURCE_CHANNEL_OPTIONS.map(o => (
                    <option key={o.channel} value={o.channel}>{o.icon}  {channelLabel(content, o.channel, o.label)}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label style={CTX_LABEL}>{funnelText(content, "canvasNameField", "Name")}</label>
                <input
                  key={`name-${contextMenu.nodeId}`}
                  defaultValue={String((contextNode.data as Record<string, unknown>).label ?? "")}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; updateNode(contextMenu.nodeId, { label: e.target.value.trim() }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
                <label style={CTX_LABEL}>{funnelText(content, "canvasStageTypeField", "Stage type")}</label>
                <select
                  key={`type-${contextMenu.nodeId}`}
                  defaultValue={String((contextNode.data as Record<string, unknown>).stageType ?? "custom")}
                  onChange={e => updateNode(contextMenu.nodeId, { stageType: e.target.value })}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
                  style={CTX_SELECT}
                >
                  {STAGE_GROUP_ORDER.map(group => (
                    <optgroup key={group} label={group}>
                      {STAGE_TYPE_OPTIONS.filter(o => o.group === group).map(o => (
                        <option key={o.type} value={o.type}>{o.icon}  {stageTypeLabel(content, o.type, o.label)}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <label style={CTX_LABEL}>{funnelText(content, "canvasNodeKindField", "Node role")}</label>
                <select
                  key={`kind-${contextMenu.nodeId}`}
                  defaultValue={String((contextNode.data as Record<string, unknown>).nodeKind ?? "stage")}
                  onChange={e => updateNode(contextMenu.nodeId, { nodeKind: e.target.value })}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
                  style={CTX_SELECT}
                >
                  {NODE_KIND_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <label style={CTX_LABEL}>{funnelText(content, "canvasUrlField", "URL / asset")}</label>
                <input
                  key={`url-${contextMenu.nodeId}`}
                  defaultValue={String((contextNode.data as Record<string, unknown>).url ?? "")}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; updateNode(contextMenu.nodeId, { url: e.target.value.trim() }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
                <label style={CTX_LABEL}>{funnelText(content, "canvasConversionRateField", "Conversion rate (%)")}</label>
                <input
                  key={`cvr-${contextMenu.nodeId}`}
                  type="number"
                  min={0.1}
                  max={100}
                  step={0.5}
                  defaultValue={Number((contextNode.data as Record<string, unknown>).conversionRate ?? 10)}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) updateNode(contextMenu.nodeId, { conversionRate: v }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={CTX_LABEL}>{funnelText(content, "canvasPublishedStatusField", "Status")}</label>
                    <select
                      key={`status-${contextMenu.nodeId}`}
                      defaultValue={String((contextNode.data as Record<string, unknown>).publishedStatus ?? "draft")}
                      onChange={e => updateNode(contextMenu.nodeId, { publishedStatus: e.target.value })}
                      onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                      onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
                      style={CTX_SELECT}
                    >
                      {PUBLISHED_STATUS_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={CTX_LABEL}>{funnelText(content, "canvasTrackingStatusField", "Tracking")}</label>
                    <select
                      key={`tracking-${contextMenu.nodeId}`}
                      defaultValue={String((contextNode.data as Record<string, unknown>).trackingStatus ?? "none")}
                      onChange={e => updateNode(contextMenu.nodeId, { trackingStatus: e.target.value })}
                      onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                      onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; }}
                      style={CTX_SELECT}
                    >
                      {TRACKING_STATUS_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={CTX_LABEL}>{funnelText(content, "canvasWaitDaysField", "Wait days")}</label>
                    <input
                      key={`wait-${contextMenu.nodeId}`}
                      type="number"
                      min={0}
                      max={365}
                      defaultValue={Number((contextNode.data as Record<string, unknown>).waitDays ?? 0)}
                      onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 0) updateNode(contextMenu.nodeId, { waitDays: v }); }}
                      onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                      style={CTX_INPUT}
                    />
                  </div>
                  <div>
                    <label style={CTX_LABEL}>{funnelText(content, "canvasDueInDaysField", "Due in days")}</label>
                    <input
                      key={`due-${contextMenu.nodeId}`}
                      type="number"
                      min={0}
                      max={365}
                      defaultValue={Number((contextNode.data as Record<string, unknown>).dueInDays ?? 14)}
                      onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 0) updateNode(contextMenu.nodeId, { dueInDays: v }); }}
                      onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                      style={CTX_INPUT}
                    />
                  </div>
                </div>
                <label style={CTX_LABEL}>{funnelText(content, "canvasOwnerField", "Owner")}</label>
                <input
                  key={`owner-${contextMenu.nodeId}`}
                  defaultValue={String((contextNode.data as Record<string, unknown>).owner ?? "")}
                  onBlur={e => { e.currentTarget.style.outline = "2px solid transparent"; updateNode(contextMenu.nodeId, { owner: e.target.value.trim() }); }}
                  onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                  style={CTX_INPUT}
                />
                <label style={CTX_CHECKBOX_ROW}>
                  <input
                    key={`optional-${contextMenu.nodeId}`}
                    type="checkbox"
                    defaultChecked={Boolean((contextNode.data as Record<string, unknown>).optionalStage)}
                    onChange={e => updateNode(contextMenu.nodeId, { optionalStage: e.target.checked })}
                  />
                  {funnelText(content, "canvasOptionalStageField", "Optional stage")}
                </label>
              </>
            )}

            {(() => {
              const data = contextNode.data as Record<string, unknown>;
              const limits = nodeBoxLimits(contextNode.type);
              const activeColor = nodeDefaultColor(contextNode);
              const currentWidth = clampNodeDimension(
                data.boxWidth,
                limits.minWidth,
                limits.maxWidth,
                limits.defaultWidth,
              );
              const currentHeight = clampNodeDimension(
                data.boxHeight,
                limits.minHeight,
                limits.maxHeight,
                limits.defaultHeight,
              );
              return (
                <div style={{ marginTop: 2, marginBottom: 10 }}>
                  <div style={{ height: 1, background: "var(--color-border-subtle, #334155)", margin: "0 0 10px" }} />
                  <label style={CTX_LABEL}>{funnelText(content, "canvasBoxColorField", "Box color")}</label>
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    {NODE_COLOR_SWATCHES.map((swatch) => (
                      <button
                        key={swatch}
                        type="button"
                        aria-label={funnelText(content, "canvasColorSwatchAria", "Set box color {color}", { color: swatch })}
                        onClick={() => updateNode(contextMenu.nodeId, { color: swatch })}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          border: activeColor === swatch ? "2px solid var(--color-text, #e2e8f0)" : "1px solid rgba(226,232,240,0.4)",
                          background: swatch,
                          cursor: "pointer",
                          boxShadow: activeColor === swatch ? `0 0 0 2px ${swatch}66` : "none",
                        }}
                      />
                    ))}
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 24,
                        borderRadius: 6,
                        border: "1px solid var(--color-border-subtle, #334155)",
                        background: "var(--color-canvas-bg, #0f172a)",
                        cursor: "pointer",
                      }}
                      title={funnelText(content, "canvasCustomColorField", "Custom color")}
                    >
                      <input
                        type="color"
                        aria-label={funnelText(content, "canvasCustomColorField", "Custom color")}
                        value={activeColor}
                        onChange={e => updateNode(contextMenu.nodeId, { color: e.target.value })}
                        style={{
                          width: 22,
                          height: 18,
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => updateNode(contextMenu.nodeId, { color: undefined })}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "1px solid var(--color-border-subtle, #334155)",
                        background: "transparent",
                        color: "var(--color-muted, #cbd5e1)",
                        fontSize: 11,
                        fontWeight: 650,
                        cursor: "pointer",
                      }}
                    >
                      {funnelText(content, "canvasResetColorButton", "Reset")}
                    </button>
                  </div>
                  <p
                    style={{
                      margin: "0 0 8px",
                      color: "var(--color-muted-dim, #64748b)",
                      fontSize: 11,
                      lineHeight: 1.45,
                    }}
                  >
                    {funnelText(content, "canvasResizeHelp", "Select a box and drag its edge or corner handles to resize it.")}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={CTX_LABEL}>{funnelText(content, "canvasBoxWidthField", "Width")}</label>
                      <input
                        key={`width-${contextMenu.nodeId}`}
                        type="number"
                        min={limits.minWidth}
                        max={limits.maxWidth}
                        step={10}
                        defaultValue={currentWidth}
                        onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                        onBlur={e => {
                          e.currentTarget.style.outline = "2px solid transparent";
                          updateNode(contextMenu.nodeId, {
                            boxWidth: clampNodeDimension(e.currentTarget.value, limits.minWidth, limits.maxWidth, limits.defaultWidth),
                          });
                        }}
                        onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                        style={CTX_INPUT}
                      />
                    </div>
                    <div>
                      <label style={CTX_LABEL}>{funnelText(content, "canvasBoxHeightField", "Height")}</label>
                      <input
                        key={`height-${contextMenu.nodeId}`}
                        type="number"
                        min={limits.minHeight}
                        max={limits.maxHeight}
                        step={10}
                        defaultValue={currentHeight}
                        onFocus={e => { e.currentTarget.style.outline = "2px solid var(--color-accent-edge, #818cf8)"; }}
                        onBlur={e => {
                          e.currentTarget.style.outline = "2px solid transparent";
                          updateNode(contextMenu.nodeId, {
                            boxHeight: clampNodeDimension(e.currentTarget.value, limits.minHeight, limits.maxHeight, limits.defaultHeight),
                          });
                        }}
                        onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") closeAllMenus(); }}
                        style={CTX_INPUT}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ height: 1, background: "var(--color-border-subtle, #334155)", margin: "4px 0 10px" }} />
            {contextNode.type === "stage" && (
              <button
                type="button"
                onClick={copyContextStageLink}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
                  width: "100%", padding: "6px 10px", marginBottom: 8,
                  borderRadius: 6, background: "rgba(37,99,235,0.12)",
                  border: "1px solid rgba(59,130,246,0.35)", color: "var(--color-text, #e2e8f0)",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                {stageLinkCopyStatus === "copied"
                  ? funnelText(content, "canvasStageLinkCopied", "Stage link copied")
                  : stageLinkCopyStatus === "failed"
                    ? funnelText(content, "sharePlanFailed", "Copy failed")
                    : funnelText(content, "canvasCopyStageLinkButton", "Copy stage link")}
                <span aria-hidden="true">-&gt;</span>
              </button>
            )}
            <button
              type="button"
              onClick={deleteContextNode}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                width: "100%", padding: "6px 10px",
                borderRadius: 6, background: "transparent",
                border: "1px solid var(--color-error, #ef4444)", color: "var(--color-error, #ef4444)",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(239,68,68,0.09)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              {funnelText(content, "canvasDeleteNodeButton", "Delete node")}
            </button>
          </div>
        </>
      ), menuPortal)}
    </div>
  );
}

export function FunnelCanvasStep({
  state,
  dispatch,
  content,
  locale,
  pendingSyncRef: externalPendingSyncRef,
  flushRef,
}: {
  state: FunnelWizardState;
  dispatch: Dispatch<Action>;
  content: FunnelPlannerContent;
  locale: AppLocale;
  pendingSyncRef?: React.MutableRefObject<boolean>;
  flushRef?: React.MutableRefObject<(() => void) | null>;
}) {
  const internalRef = useRef(false);
  const pendingSyncRef = externalPendingSyncRef ?? internalRef;
  return (
    <>
      <style>{CANVAS_STYLE_TOKENS}</style>
      <ReactFlowProvider>
        <CanvasInner
          state={state}
          dispatch={dispatch}
          content={content}
          locale={locale}
          pendingSyncRef={pendingSyncRef}
          flushRef={flushRef}
        />
      </ReactFlowProvider>
    </>
  );
}
